import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, apiError, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, Search, Download, Upload, AlertTriangle } from "lucide-react";
import { ImportDialog } from "@/components/ImportDialog";
import { findDuplicateRows } from "@/lib/dupes";
import { downloadTemplate as downloadTemplate_, parseSheet } from "@/lib/sheet";
import { t, tf } from "@/lib/i18n";

const empty = { name: "", code: "", category: "", description: "", price: 0, stock: 0, low_stock_threshold: 10, vat_rate: 22, discount: 0, image_url: "", pack_size: 1 };
const PRODUCT_COLUMNS = [
  { key: "name", label: "Nome prodotto", width: 28 },
  { key: "code", label: "Codice" },
  { key: "category", label: "Categoria" },
  { key: "description", label: "Descrizione", width: 30 },
  { key: "price", label: "Prezzo", type: "number" },
  { key: "stock", label: "Giacenza", type: "int" },
  { key: "low_stock_threshold", label: "Soglia scorta minima", type: "int" },
  { key: "pack_size", label: "Pezzi per collo", type: "int" },
  { key: "vat_rate", label: "IVA %", type: "number" },
  { key: "discount", label: "Sconto %", type: "number" },
  { key: "image_url", label: "URL immagine", width: 30 },
];

export default function Products() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? (await api.put(`/products/${editing.product_id}`, payload)).data
              : (await api.post("/products", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato"); setOpen(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/products/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); toast.success(t("Prodotto eliminato")); },
  });
  const bulk = useMutation({
    mutationFn: async ({ rows, mode }) => (await api.post("/products/bulk", { products: rows, mode })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(tf(d.updated ? "{n} prodotti aggiunti, {u} aggiornati" : "{n} prodotti aggiunti", { n: d.inserted, u: d.updated }));
      setPending(null);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const [pending, setPending] = useState(null);
  const [importMode, setImportMode] = useState("upsert");

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };
  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const str = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    save.mutate({ ...form, price: Number(form.price), stock: Number(form.stock), low_stock_threshold: Number(form.low_stock_threshold), vat_rate: Number(form.vat_rate), discount: Number(form.discount), pack_size: Math.max(1, parseInt(form.pack_size, 10) || 1) });
  };

  const downloadTemplate = () => {
    downloadTemplate_(PRODUCT_COLUMNS, ["Acqua Naturale 1L", "ACQ-001", "Bevande", "Confezione 6 pezzi", 3.5, 200, 20, 6, 10, 0, ""], "modello_prodotti.xlsx", "Prodotti");
    toast.success(t("Modello Excel scaricato"));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let rows;
    try {
      rows = (await parseSheet(file, PRODUCT_COLUMNS)).filter((r) => r.name && r.code);
      const dups = findDuplicateRows(rows, (r) => r.code.trim());
      if (dups.length) {
        toast.error(tf("Codici duplicati nel file, correggi le righe: {list}", { list: dups.slice(0, 8).join("; ") }), { duration: 10000 });
        return;
      }
    } catch {
      toast.error(t("File non leggibile. Usa il modello Excel o un CSV."));
      return;
    }
    if (!rows.length) { toast.error(t("Nessuna riga valida (servono almeno nome e codice)")); return; }
    setImportMode("upsert");
    setPending(rows);
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || "").toLowerCase().includes(search.toLowerCase()));
  const lowCount = products.filter((p) => (p.stock ?? 0) <= (p.low_stock_threshold ?? 10)).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Catalogo prodotti")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{products.length} {t("prodotti")}{lowCount ? ` · ${lowCount} in esaurimento` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-template-btn"><Download className="h-4 w-4" /> {t("Modello Excel")}</Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="import-products-btn"><Upload className="h-4 w-4" /> {t("Importa")}</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          <Button onClick={openNew} className="gap-2 font-semibold" data-testid="add-product-btn"><Plus className="h-4 w-4" /> {t("Nuovo")}</Button>
        </div>
      </div>
      <ImportDialog rows={pending} mode={importMode} setMode={setImportMode} existing={products}
        onClose={() => setPending(null)} onConfirm={() => bulk.mutate({ rows: pending, mode: importMode })} busy={bulk.isPending} />

      {lowCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm flex items-center gap-2" data-testid="low-stock-alert-badge">
          <AlertTriangle className="h-4 w-4" /> {lowCount} {t("prodotti sono sotto la soglia di scorta minima.")}
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="product-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Cerca per nome, codice o categoria")} className="pl-9" />
      </div>

      {isLoading ? <p className="text-muted-foreground">{t("Caricamento…")}</p> : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center">
          <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("Nessun prodotto. Aggiungi o importa il catalogo via CSV.")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p, i) => {
            const low = (p.stock ?? 0) <= (p.low_stock_threshold ?? 10);
            return (
              <motion.div key={p.product_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }} data-testid={`product-card-${p.product_id}`} className="bg-white border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-all">
                <div className="h-32 bg-secondary relative overflow-hidden">
                  {p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-muted-foreground"><Package className="h-8 w-8" /></div>}
                  {p.discount > 0 && <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">-{p.discount}%</span>}
                  {low && <span className="absolute top-2 right-2 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {t("Scorta")}</span>}
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0"><p className="font-semibold truncate">{p.name}</p><p className="text-xs font-mono-j text-muted-foreground">{p.code}</p></div>
                    <span className="text-xs bg-secondary px-2 py-0.5 rounded-full text-muted-foreground whitespace-nowrap">{p.category || "—"}</span>
                  </div>
                  <div className="flex items-end justify-between pt-1">
                    <p className="font-display text-xl font-extrabold text-primary">{eur(p.price)}</p>
                    <p className={`text-xs ${low ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>{t("Stock")} {p.stock} {t("/ soglia")} {p.low_stock_threshold ?? 10}{(p.pack_size || 1) > 1 ? ` · ${tf("collo da {n} pz", { n: p.pack_size })}` : ""}</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`}><Pencil className="h-3.5 w-3.5" /> {t("Modifica")}</Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove.mutate(p.product_id)} data-testid={`delete-product-${p.product_id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? t("Modifica prodotto") : t("Nuovo prodotto")}</DialogTitle>
            <DialogDescription>{t("Compila i campi del prodotto a catalogo.")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2"><Label>{t("Nome *")}</Label><Input data-testid="product-name-input" required value={form.name} onChange={str("name")} /></div>
              <div className="space-y-2"><Label>{t("Codice *")}</Label><Input data-testid="product-code-input" required value={form.code} onChange={str("code")} /></div>
              <div className="space-y-2"><Label>{t("Categoria")}</Label><Input data-testid="product-category-input" value={form.category} onChange={str("category")} /></div>
              <div className="space-y-2"><Label>{t("Prezzo (€) *")}</Label><Input data-testid="product-price-input" type="number" step="0.01" min="0" required value={form.price} onChange={num("price")} /></div>
              <div className="space-y-2"><Label>{t("Stock")}</Label><Input data-testid="product-stock-input" type="number" min="0" value={form.stock} onChange={num("stock")} /></div>
              <div className="space-y-2"><Label>{t("Pezzi per collo")}</Label><Input data-testid="product-pack-size-input" type="number" min="1" value={form.pack_size ?? 1} onChange={num("pack_size")} /></div>
              <div className="space-y-2"><Label>{t("Soglia scorta minima")}</Label><Input data-testid="product-threshold-input" type="number" min="0" value={form.low_stock_threshold} onChange={num("low_stock_threshold")} /></div>
              <div className="space-y-2"><Label>{t("IVA %")}</Label><Input data-testid="product-vat-input" type="number" step="0.1" min="0" value={form.vat_rate} onChange={num("vat_rate")} /></div>
              <div className="space-y-2"><Label>{t("Sconto %")}</Label><Input data-testid="product-discount-input" type="number" step="0.1" min="0" max="100" value={form.discount} onChange={num("discount")} /></div>
              <div className="space-y-2 col-span-2"><Label>{t("URL immagine")}</Label><Input data-testid="product-image-input" value={form.image_url} onChange={str("image_url")} placeholder={t("https://…")} /></div>
              <div className="space-y-2 col-span-2"><Label>{t("Descrizione")}</Label><Textarea data-testid="product-description-input" value={form.description} onChange={str("description")} rows={2} /></div>
            </div>
            <DialogFooter><Button type="submit" disabled={save.isPending} className="font-semibold" data-testid="save-product-btn">{save.isPending ? t("Salvataggio…") : t("Salva")}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
